<script setup lang="ts">
import { computed } from 'vue';
import { useMainStore } from '../store';
import { getTemplate } from '../templates';
import QuestionBlock from './blocks/QuestionBlock.vue';
import GateBlock from './blocks/GateBlock.vue';
import QuestionGateBlock from './blocks/QuestionGateBlock.vue';
import FormGateBlock from './blocks/FormGateBlock.vue';

// DEV-1135 flow engine. Owns navigation state (store.flowIndex) over the
// validated screens; renders the template's shell around the core blocks.
// Only mounted when store.flowMode === 'flow' (App.vue), which the store only
// sets after validateFlowConfig + template-registry checks passed — so
// template resolution here cannot miss.
const store = useMainStore();

const template = computed(() => getTemplate(store.templateId)!);
const screen = computed(() => store.currentFlowScreen);
const progress = computed(() => store.flowQuestionProgress);

// Color template (DEV-1135): every template consumes the quiz's publinker
// custom_colors through three semantic CSS vars, so operators re-skin a
// template from the quiz form without touching code.
//   dark    → --tpl-primary   (bands, titles, CTA, progress fill)
//   lighten → --tpl-secondary (progress track, answer/input borders, accents)
//   light   → --tpl-bg        (page background)
//   header  → --tpl-header-bg (OPTIONAL header-band override — for sites whose
//             logo is invisible on the primary color, e.g. minhapme's dark
//             purple logo on a #222 band; templates fall back to primary)
// Templates declare their reference palette as var() fallbacks, so a quiz
// without custom_colors renders the template's original look.
const colorVars = computed(() => {
  const colors = (store.quizData as { custom_colors?: Record<string, string> })
    .custom_colors;
  const vars: Record<string, string> = {};
  const hex = /^#[0-9a-fA-F]{3,8}$/;
  if (colors?.dark && hex.test(colors.dark)) vars['--tpl-primary'] = colors.dark;
  if (colors?.lighten && hex.test(colors.lighten)) vars['--tpl-secondary'] = colors.lighten;
  if (colors?.light && hex.test(colors.light)) vars['--tpl-bg'] = colors.light;
  if (colors?.header && hex.test(colors.header)) vars['--tpl-header-bg'] = colors.header;
  return vars;
});

// Answered history for conversation-style templates (chat shells render the
// prior Q&A trail as bubbles). Pairs of question title + chosen answer label,
// in answer order.
const history = computed(() =>
  store.questionAnswers.map(([qId, value]) => {
    const q = store.quizData.questions?.find((qq) => qq.id === qId);
    const answer = q?.answers?.find((a) => Array.isArray(a) && a[0] === value);
    return {
      question: q?.title || '',
      answer: answer ? String(answer[1]) : String(value),
    };
  })
);

// Hero (title/sub_title) shows on the first question screen only — the UTUA
// convention; shells receive the flag and decide placement.
const showHero = computed(
  () =>
    (screen.value?.type === 'question' || screen.value?.type === 'question-gate') &&
    progress.value.current === 1
);

// Back navigation across question screens only (gate and beyond are forward-
// only; re-answering replaces via recordFlowAnswer).
const canGoBack = computed(() => {
  if (store.flowIndex === 0) return false;
  if (screen.value?.type !== 'question') return false;
  return store.flowScreens[store.flowIndex - 1]?.type === 'question';
});

const advance = () => {
  if (store.flowIndex < store.flowScreens.length - 1) {
    store.flowIndex += 1;
  }
};

const goBack = () => {
  if (canGoBack.value) store.flowIndex -= 1;
};
</script>

<template>
  <component
    :is="template.shell"
    :style="colorVars"
    :quiz="store.quizData"
    :progress="progress"
    :can-go-back="canGoBack"
    :show-hero="showHero"
    :is-gate="screen?.type === 'gate' || screen?.type === 'question-gate' || screen?.type === 'form-gate'"
    :history="history"
    @back="goBack"
  >
    <QuestionBlock
      v-if="screen?.type === 'question'"
      :key="screen.id"
      :screen="screen"
      :template="template"
      @advance="advance"
    />
    <GateBlock v-else-if="screen?.type === 'gate'" :key="screen.id" :template="template" />
    <QuestionGateBlock
      v-else-if="screen?.type === 'question-gate'"
      :key="screen.id"
      :screen="screen"
      :template="template"
    />
    <FormGateBlock
      v-else-if="screen?.type === 'form-gate'"
      :key="screen.id"
      :screen="screen"
      :template="template"
    />
  </component>
</template>
